import React from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { LayoutDashboard, Users, Send, FileText, Upload, BarChart3, Settings, LogOut, BookOpen, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const navItems = [
  { to: '/app', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/app/contacts', label: 'Contacts', icon: Users },
  { to: '/app/campaigns', label: 'Campaigns', icon: Send },
  { to: '/app/templates', label: 'Templates', icon: FileText },
  { to: '/app/upload', label: 'Import', icon: Upload },
  { to: '/app/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/app/settings', label: 'Settings', icon: Settings },
  { to: '/help', label: 'Help', icon: BookOpen, external: false },
];

export default function DashboardLayout() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const name = user?.name || 'You';
  const email = user?.email || '';
  const initials = name.split(' ').filter(Boolean).map(p => p[0]).join('').slice(0,2).toUpperCase();
  return (
    <div className="min-h-screen bg-[#0a0a0b] text-zinc-100 flex">
      <aside className="w-64 shrink-0 border-r border-zinc-900 h-screen sticky top-0 flex flex-col">
        <div className="px-5 h-16 flex items-center gap-2 border-b border-zinc-900">
          <div className="w-8 h-8 rounded-full border border-[#d4a94a] flex items-center justify-center">
            <span className="font-display text-[#d4a94a] text-lg font-semibold leading-none">R</span>
          </div>
          <span className="font-display text-lg">Reachly</span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? 'bg-[#1a1815] text-[#f5d67a] border border-[#3a2f18]' : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60'
                }`
              }
            >
              <item.icon size={16} /> {item.label}
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink
              to="/app/admin/help"
              data-testid="nav-admin-help"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? 'bg-[#1a1815] text-[#f5d67a] border border-[#3a2f18]' : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60'
                }`
              }
            >
              <ShieldCheck size={16}/> Admin · Help
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t border-zinc-900">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-[#1a1815] border border-[#3a2f18] flex items-center justify-center text-[#f5d67a] text-sm font-semibold">
              {initials || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{name}</div>
              <div className="text-xs text-zinc-500 truncate">{email}</div>
            </div>
            <button onClick={() => { logout(); navigate('/'); }} className="text-zinc-500 hover:text-zinc-100"><LogOut size={16} /></button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
